import * as React from 'react';

import { cn } from '@/lib/utils';
import { trackWebsiteEvent } from '@/lib/analytics';

/** Calendly inline-widget URL (event + theme params, per the embed snippet). */
const CALENDLY_URL =
  'https://calendly.com/onchainden/demo?hide_event_type_details=1&hide_gdpr_banner=1&text_color=0f172a&primary_color=0f172a';

const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

/** Defer the third-party script so it does not compete with the first paint. */
const INITIAL_LOAD_DELAY_MS = 600;
/** Five empty weeks suggest a calendar without showing invented availability. */
const CALENDAR_PLACEHOLDER_CELLS = 35;

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget(options: {
        url: string;
        parentElement: HTMLElement;
        resize?: boolean;
      }): void;
    };
  }
}

/** Load the Calendly widget script once (shared, idempotent). */
let calendlyScriptPromise: Promise<void> | null = null;
function loadCalendly(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Calendly) return Promise.resolve();
  if (calendlyScriptPromise) return calendlyScriptPromise;
  calendlyScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CALENDLY_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      calendlyScriptPromise = null; // allow a retry
      reject(new Error('Failed to load the Calendly widget.'));
    };
    document.head.appendChild(script);
  });
  return calendlyScriptPromise;
}

type DemoContext = {
  cta_location: string;
  demo_page_path: string;
  ad_selected_use_case?: string;
};

function CalendlyLoading() {
  return (
    <div
      role="status"
      aria-label="Loading scheduler"
      className="absolute inset-0 flex items-center justify-center bg-white px-6 py-8"
    >
      <div className="w-full max-w-xs">
        <div
          aria-hidden="true"
          className="animate-pulse rounded-2xl border border-hairline bg-ink-50/40 p-5 motion-reduce:animate-none"
        >
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="h-3 w-28 rounded-full bg-ink-200/70" />
            <div className="flex gap-2">
              <div className="size-6 rounded-full bg-ink-100" />
              <div className="size-6 rounded-full bg-ink-100" />
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: CALENDAR_PLACEHOLDER_CELLS }, (_, index) => (
              <div
                key={index}
                className="aspect-square rounded-md bg-ink-100/80"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Shared scheduler for the global dialog and campaign landing page. Hidden
 * dialog widgets stay warm; active schedulers are never refreshed mid-booking.
 * Each instance accepts messages only from its own live Calendly iframe.
 */
export function CalendlyWidget({
  active,
  analyticsContext,
  trackOpenOnView = false,
  onReadyChange,
  className,
  height = 'min(600px, 80vh)',
  autoResize = false,
}: {
  active: boolean;
  analyticsContext: DemoContext;
  trackOpenOnView?: boolean;
  onReadyChange?: (ready: boolean) => void;
  className?: string;
  height?: React.CSSProperties['height'];
  autoResize?: boolean;
}) {
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  // Latest activity/context for the message callback, without
  // re-subscribing or recreating an iframe when a parent renders.
  const settingsRef = React.useRef({ active, analyticsContext });
  settingsRef.current = { active, analyticsContext };
  const bookedEvents = React.useRef(new Set<string>());
  const viewed = React.useRef(false);

  const updateReady = React.useCallback(
    (value: boolean) => {
      setReady(value);
      onReadyChange?.(value);
    },
    [onReadyChange]
  );

  // Initialize one iframe per host and preserve its loaded calendar and booking
  // state across dialog opens. Reveal it only after Calendly reports readiness.
  const initializeWidget = React.useCallback(() => {
    const el = hostRef.current;
    if (!el || !window.Calendly || el.querySelector('iframe')) return;
    updateReady(false);
    setFailed(false);
    window.Calendly.initInlineWidget({
      url: CALENDLY_URL,
      parentElement: el,
      // The campaign page grows with each booking step instead of nesting a
      // scrollbar. Dialogs keep their viewport-limited height.
      resize: autoResize,
    });
    const frame = el.querySelector('iframe');
    frame?.addEventListener(
      'error',
      () => {
        // A replaced iframe must not change the current scheduler's state.
        if (hostRef.current?.querySelector('iframe') !== frame) return;
        setFailed(true);
        updateReady(true);
      },
      {
        once: true,
      }
    );
  }, [updateReady, autoResize]);

  // Preload the closed dialog after the first paint. Opening it sooner starts
  // immediately and joins the same script request. Never discard a warm iframe
  // on a timer or tab switch: that makes the next click wait for Calendly again.
  React.useEffect(() => {
    let cancelled = false;
    const initialize = () => {
      void loadCalendly()
        .then(() => {
          if (!cancelled) initializeWidget();
        })
        .catch(() => {
          if (!cancelled) {
            setFailed(true);
            updateReady(true); // Replace the loading panel with the booking link.
          }
        });
    };
    const warmId = active
      ? undefined
      : window.setTimeout(initialize, INITIAL_LOAD_DELAY_MS);
    if (active) initialize();

    return () => {
      cancelled = true;
      window.clearTimeout(warmId);
    };
  }, [active, initializeWidget, updateReady]);

  // Readiness and booking activity belong only to this instance's live iframe.
  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      // Only the live Calendly iframe can report scheduler activity.
      const frame = hostRef.current?.querySelector('iframe');
      if (
        e.origin !== 'https://calendly.com' ||
        !frame?.contentWindow ||
        e.source !== frame.contentWindow ||
        !e.data ||
        typeof e.data !== 'object'
      )
        return;

      const reservedHeight = hostRef.current?.parentElement?.clientHeight ?? 0;
      const reportedHeight = Number.parseFloat(e.data.payload?.height);
      // A calendar outside the viewport can report its final size before its
      // view event. Compare with our reserved panel height so the tiny loading
      // frame cannot reveal itself, while offscreen/warm calendars can be ready.
      const calendarHasSize =
        e.data.event === 'calendly.page_height' &&
        reservedHeight > 0 &&
        Number.isFinite(reportedHeight) &&
        reportedHeight >= reservedHeight;
      if (e.data.event === 'calendly.event_type_viewed' || calendarHasSize) {
        // An iframe load can still show Calendly's own spinner. Reveal the
        // rendered calendar, including after a slow recovery.
        setFailed(false);
        updateReady(true);
      }
      // The widget preloads while hidden. Those messages are
      // not visitor interactions and must not enter the demo funnel.
      if (!settingsRef.current.active) return;
      const context = settingsRef.current.analyticsContext;
      if (e.data.event === 'calendly.date_and_time_selected') {
        trackWebsiteEvent('demo_time_selected', context);
      }
      if (
        e.data.event === 'calendly.event_scheduled' &&
        typeof e.data.payload?.event?.uri === 'string' &&
        !bookedEvents.current.has(e.data.payload.event.uri)
      ) {
        bookedEvents.current.add(e.data.payload.event.uri);
        // Use the event URI only for local deduplication. Do not forward
        // Calendly's invitee details or booking payload to analytics.
        trackWebsiteEvent('demo_booked', context);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [updateReady]);

  // Unlike a dialog click, loading the ad page does not mean the visitor has
  // seen its scheduler. Count an inline demo opening once it is ready and visible.
  React.useEffect(() => {
    const host = hostRef.current;
    if (
      !active ||
      !ready ||
      failed ||
      !trackOpenOnView ||
      viewed.current ||
      !host
    )
      return;
    const observer = new IntersectionObserver((entries) => {
      if (!viewed.current && entries.some((entry) => entry.isIntersecting)) {
        viewed.current = true;
        trackWebsiteEvent('demo_opened', settingsRef.current.analyticsContext);
        observer.disconnect();
      }
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, [active, ready, failed, trackOpenOnView]);

  const showCalendar = ready && !failed;
  return (
    <div
      aria-busy={!ready}
      className={cn('relative overflow-hidden bg-white', className)}
      style={{ minHeight: height }}
    >
      {/* Persistent white widget panel. The host holds the prerendered Calendly
          iframe; the loading panel covers it until the calendar has rendered. Reserve
          height even when Calendly briefly reports a tiny loading frame, then
          let auto-resizing booking steps expand with the page. */}
      <div
        ref={hostRef}
        aria-hidden={!showCalendar}
        inert={!showCalendar ? true : undefined}
        className={cn(
          'transition-opacity duration-300 motion-reduce:transition-none',
          showCalendar ? 'opacity-100' : 'opacity-0'
        )}
        style={{ minWidth: 'min(320px, 100%)', height }}
      />
      {!ready && <CalendlyLoading />}
      {failed && (
        <div className="absolute inset-0 grid place-content-center gap-3 px-6 text-center text-sm text-ink-500">
          <p>The embedded scheduler couldn’t load.</p>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-ink-900 underline underline-offset-4"
          >
            Book directly on Calendly
          </a>
        </div>
      )}
      <noscript>
        <a href={CALENDLY_URL}>Book a demo on Calendly</a>
      </noscript>
    </div>
  );
}
